import { UserRoles } from '@/constants';
import { BrokerStatus } from '@/constants/BrokerStatus';
import { Messages } from '@/constants/messages';
import type { INotification } from '@/interfaces';
import { BrokerService, HubsService, UserService } from '@/services';
import type { User } from 'firebase/auth';
import { defineStore } from 'pinia';

export const UserStore = defineStore('user', {
	state: () => ({
		user: null as User | null,
		showLoginPrompt: true,
		userClaims: {
			Role: '',
			BrokerId: '',
			Email: '',
			Document: '',
			Id: '',
			Name: '',
			Phone: '',
			aud: '',
			auth_time: '',
			email: '',
			email_verified: '',
			exp: '',
			firebase: '',
			iss: '',
			name: '',
			sub: '',
			user_id: '',
		},
		theme: localStorage.getItem('theme') || 'light',
		notifications: new Array<INotification<any>>(),
		/** Status do parceiro de quem está logado — só carregado para os papéis de parceiro. */
		partnerBrokerStatus: null as BrokerStatus | null,
	}),

	actions: {
		setUser(user: User) {
			this.showLoginPrompt = false;
			this.user = user;
		},

		showLogin() {
			this.showLoginPrompt = true;
		},

		async setClaims(claims: any) {
			this.userClaims = claims;

			// amplitude.setUserId(claims.Id);

			// const identifyEvent = new amplitude.Identify();
			// identifyEvent.set('brokerId', claims.BrokerId);

			// amplitude.identify(identifyEvent);
			// amplitude.setGroup('role', claims.Role);
		},

		/**
		 * Carrega o status do parceiro de quem está logado (BROKER_ADMIN/USER/AGENT), lido do
		 * `GET /brokers/{BrokerId}` — o mesmo que a cotação de Seguro Incêndio já chama para esses
		 * papéis. Em erro fica `null` e libera: a API recusa a criação (400) de qualquer jeito, e uma
		 * falha aqui não pode travar a operação de um parceiro ativo.
		 */
		async loadPartnerBrokerStatus() {
			const brokerId = this.userClaims.BrokerId;
			if (!brokerId || !UserRoles.BrokersRoles.includes(this.userClaims.Role)) {
				this.partnerBrokerStatus = null;
				return;
			}

			try {
				const response = await BrokerService.getBroker(brokerId);
				this.partnerBrokerStatus = response.data?.status ?? null;
			} catch (e) {
				console.error('Falha ao carregar o status do parceiro', e);
				this.partnerBrokerStatus = null;
			}
		},

		setUserTheme(theme: string) {
			localStorage.setItem('theme', theme);
		},

		toggleTheme() {
			this.theme = this.theme === 'light' ? 'dark' : 'light';
			this.setUserTheme(this.theme);
		},

		async getNotifications() {
			const response = await UserService.getNotifications();
			this.setNotifications(response.data);
		},

		async initNofiicationsHub() {
			const hub = await HubsService.initNoficationsHub();
			this.getNotifications();
			hub.onNotificationReceived(() => this.getNotifications());
		},

		async readNotifications() {
			if (this.hasUnreadNotification) {
				const response = await UserService.readNotifications();
				this.setNotifications(response.data);
			}
		},

		setNotifications(notifications: INotification<any>[]) {
			this.notifications = notifications;

			const title = window.document.title.replace(/\([0-9]+\)/g, '');

			if (this.hasUnreadNotification) {
				window.document.title = `(${this.countUnreadNotifications}) ${title}`;
			} else {
				window.document.title = title;
			}
		},
	},

	getters: {
		role: (state) => state.userClaims.Role,
		isUserAdmin: (state) => state.userClaims.Role === UserRoles.Admin,
		isExternalPartner: (state) => state.userClaims.Role === UserRoles.ExternalPartner,
		isReadOnly: (state) => state.userClaims.Role === UserRoles.ExternalPartner,
		canReadAsAdmin: (state) =>
			state.userClaims.Role === UserRoles.Admin ||
			state.userClaims.Role === UserRoles.ExternalPartner,
		isAllowedForBackOffice: (state) =>
			state.userClaims.Role === UserRoles.Admin ||
			state.userClaims.Role === UserRoles.User ||
			state.userClaims.Role === UserRoles.ExternalPartner,
		isUser: (state) => state.userClaims.Role === UserRoles.User,
		isBrokerAdmin: (state) => state.userClaims.Role === UserRoles.BrokerAdmin,
		isBrokerAgent: (state) => state.userClaims.Role === UserRoles.BrokerAgent,
		isBusinessConsultant: (state) => state.userClaims.Role === UserRoles.BUSINESS_CONSULTANT,
		isBusinessManager: (state) => state.userClaims.Role === UserRoles.BUSINESS_MANAGER,
		isBrokerUser: (state) =>
			state.userClaims.Role === UserRoles.BrokerAdmin ||
			state.userClaims.Role === UserRoles.BrokerAgent,
		appTheme: (state) => state.theme,
		hasUnreadNotification: (state) => state.notifications.some((x) => x.readAt == null),
		countUnreadNotifications: (state) => state.notifications.filter((x) => x.readAt == null).length,
		isBusinessHead: (state) => state.userClaims.Role === UserRoles.BUSINESS_HEAD,
		/** Parceiro em "Pendente assinatura": ainda não assinou o Termo de Parceria na ClickSign. */
		isPartnerWaitingSignature: (state) =>
			state.partnerBrokerStatus === BrokerStatus.WaitingSignature,
		/**
		 * Motivo de "Nova proposta", "Nova ocorrência" e afins estarem bloqueados para o parceiro
		 * ainda não ativado ("Pendente assinatura" ou "Pendente"), ou `null`. A API devolve 400 na
		 * criação; isto só desabilita os botões e explica o motivo no tooltip. A regra mora só aqui
		 * para casar com o `operationBlockedMessage` da PR #467 (usuário/parceiro inativo).
		 */
		partnerCreationBlockedMessage: (state): string | null => {
			switch (state.partnerBrokerStatus) {
				case BrokerStatus.Pending:
					return Messages.PARTNER_PENDING_ACTIVATION;
				case BrokerStatus.WaitingSignature:
					return `${Messages.PARTNER_PENDING_ACTIVATION} ${Messages.PARTNER_PENDING_SIGNATURE}`;
				default:
					return null;
			}
		},
		isCommercial: (state) =>
			state.userClaims.Role === UserRoles.BUSINESS_MANAGER ||
			state.userClaims.Role === UserRoles.BUSINESS_HEAD ||
			state.userClaims.Role === UserRoles.BUSINESS_CONSULTANT,
	},
});

export default UserStore;
